/**
 * The authored design-token set — the single source of colour for the CRM.
 *
 * Every token declares its value for BOTH themes in one place. The CSS custom
 * properties written to `:root`, the `colors.values` entries that mirror them,
 * and the Tailwind utilities in `tailwind-theme-preset.cjs` are all downstream
 * of this table. Nothing else should hand-author a colour.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * Colour used to live inside the class strings in `tailwind-classes.ts`, where
 * "what a surface means" and "which grey it is" were fused into one value:
 *
 *     bgPrimary: 'bg-white dark:bg-gray-800'
 *
 * That has two consequences. Changing the design language means editing every
 * token string by hand and re-verifying each in two modes. And because each
 * string carries its own `dark:` variant, the two themes drift independently —
 * which is exactly what happened. See docs/claude/style-guide/01-token-architecture.md.
 *
 * Now a class token names a role and the role resolves to a variable:
 *
 *     bgPrimary: 'bg-surface'        -->  var(--bg-surface)
 *
 * Call sites are untouched — `colors.classes.bgPrimary` still means the same
 * thing in all 620 places it appears. Only the layer underneath changed.
 *
 * ---------------------------------------------------------------------------
 * NAME MAPPING — read this before hunting for a token
 * ---------------------------------------------------------------------------
 *
 * The foreground ramp is named by EMPHASIS, not by the legacy class-token
 * names, because "primary" previously meant both the brand blue and near-black
 * body text (`bg-primary` was blue while `text-primary` was near-black, with no
 * error). Retiring the name is decision D2. During the migration the class
 * tokens keep their old names, so the two vocabularies do not line up 1:1:
 *
 *     classes.textPrimary    -->  --fg-default    strongest, body text
 *     classes.textSecondary  -->  --fg-muted      supporting text
 *     classes.textMuted      -->  --fg-subtle     metadata
 *     classes.textTertiary   -->  --fg-faint      de-emphasised, placeholders
 *
 * `classes.textMuted` resolving to `--fg-subtle` looks like a bug and is not.
 * The class-token names disappear when `colors.classes` is deleted at the end
 * of the migration; the variable names are the ones that survive.
 *
 * ---------------------------------------------------------------------------
 * TRANSITIONAL TOKENS
 * ---------------------------------------------------------------------------
 *
 * Tokens marked `TRANSITIONAL` exist only because the current product has two
 * treatments for one idea (a yellow warning and an amber one, a red-600 danger
 * fill and a red-500 one). They are recorded rather than silently unified so
 * the choice is made deliberately during the button/badge consolidation, not
 * lost here. Do not build anything new on them.
 */

export interface TokenValue {
	/** Value in the light theme. */
	light: string;
	/** Value in the dark theme. */
	dark: string;
}

/**
 * Values are authored to match what the product renders today, EXCEPT for the
 * corrections listed in `TOKEN_CORRECTIONS` below. Hue changes (the brand
 * accent, the status palette) are a separate step and are not made here.
 */
export const designTokens = {
	// -------------------------------------------------------------------------
	// Surfaces
	//
	// Elevation reads the same way in both themes: a raised surface is LIGHTER
	// than the canvas it sits on, a sunken one is darker. That rule is what
	// makes a card look like a card in dark mode.
	// -------------------------------------------------------------------------

	/** The page itself, behind all content. */
	'--bg-canvas': { light: '#F9FAFB', dark: '#111827' },
	/** Cards, panels, table rows, inputs, modals — the default content surface. */
	'--bg-surface': { light: '#FFFFFF', dark: '#1F2937' },
	/** Hover state for an interactive row or panel on `--bg-surface`. */
	'--bg-surface-hover': { light: '#F9FAFB', dark: '#374151' },
	/** Menus, popovers and dropdowns, which float ABOVE a surface. */
	'--bg-raised': { light: '#FFFFFF', dark: '#283244' },
	/** Wells, table headers — recessed BELOW the surface. */
	'--bg-sunken': { light: '#F3F4F6', dark: '#161E2B' },
	/** A highlighted or selected row, option or message bubble. */
	'--bg-subtle': { light: '#F3F4F6', dark: '#374151' },
	/** A filled neutral control: secondary button, filter pill. */
	'--bg-control': { light: '#E5E7EB', dark: '#374151' },
	/** Hover for `--bg-control`; also the muted/disabled fill. */
	'--bg-control-strong': { light: '#D1D5DB', dark: '#4B5563' },
	/** Zebra striping. Alternates slightly ABOVE the row in dark, unlike sunken. */
	'--bg-row-stripe': { light: '#F3F4F6', dark: '#252F3F' },
	/** A chip or panel deliberately inverted against the current theme. */
	'--bg-inverse': { light: '#111827', dark: '#F3F4F6' },
	/** Tooltips are dark in BOTH themes — they read as an overlay, not a surface. */
	'--bg-tooltip': { light: '#1F2937', dark: '#374151' },
	/** Chart tooltip: same idea, but translucent so the series stays visible. */
	'--bg-tooltip-chart': {
		light: 'rgba(255, 255, 255, 0.95)',
		dark: 'rgba(31, 41, 55, 0.95)',
	},
	/** Modal backdrop — translucent, blurs what is behind it. */
	'--bg-overlay': {
		light: 'rgba(17, 24, 39, 0.4)',
		dark: 'rgba(0, 0, 0, 0.6)',
	},
	/** Opaque full-cover scrim. */
	'--bg-scrim': { light: '#111827', dark: '#000000' },

	// -------------------------------------------------------------------------
	// Foreground ramp
	//
	// Four emphasis levels, distinct in BOTH themes. Dark mode previously
	// collapsed levels 2 and 3 onto the same grey, flattening the hierarchy
	// across ~5,000 call sites.
	// -------------------------------------------------------------------------

	/** Body copy, headings, table cells. */
	'--fg-default': { light: '#111827', dark: '#F3F4F6' },
	/** Supporting text — labels, descriptions, secondary columns. */
	'--fg-muted': { light: '#4B5563', dark: '#D1D5DB' },
	/** Metadata — timestamps, counts, helper text. */
	'--fg-subtle': { light: '#6B7280', dark: '#9CA3AF' },
	/** Deliberately faint — disabled labels, low-priority hints. */
	'--fg-faint': { light: '#9CA3AF', dark: '#6B7280' },
	/** Input placeholders. Identical in both themes by design. */
	'--fg-placeholder': { light: '#9CA3AF', dark: '#9CA3AF' },
	/** Text on `--bg-inverse`. */
	'--fg-on-inverse': { light: '#F3F4F6', dark: '#111827' },
	/**
	 * Text on any solid dark fill — a danger button, a tooltip, a filled badge.
	 * Fixed white in both themes, because the fill it sits on is fixed too.
	 * The accent has its own `--accent-fg`, and warning has its own, because
	 * those two are expected to diverge from white.
	 */
	'--fg-on-fill': { light: '#FFFFFF', dark: '#FFFFFF' },

	// -------------------------------------------------------------------------
	// Borders
	// -------------------------------------------------------------------------

	/** The default hairline between surfaces. */
	'--border-default': { light: '#E5E7EB', dark: '#4B5563' },
	/** A deliberately heavier rule — section dividers, emphasised outlines. */
	'--border-strong': { light: '#D1D5DB', dark: '#6B7280' },
	/** Barely-there separation inside a dense group. */
	'--border-subtle': { light: '#F3F4F6', dark: '#1F2937' },
	/** Disabled controls, list dividers, chart-tooltip outline. */
	'--border-muted': { light: '#E5E7EB', dark: '#374151' },
	/** The outline a field takes while focused. Follows the accent. */
	'--border-focus': { light: '#0066FF', dark: '#579AFF' },

	// -------------------------------------------------------------------------
	// Accent (brand) — #0066FF
	//
	// The Workd brand blue, same fill in both themes. "Info" is not a separate
	// hue; it reuses the accent.
	//
	// Everything below except the fill itself is DERIVED, by
	// scripts/style-audit/derive-palette.mjs, from the fill plus the surface it
	// has to be legible against. Re-run that script rather than hand-adjusting
	// one of these — the ratios are load-bearing and picking a value by eye is
	// what produced a set of chips ranging from 4.4:1 to 7.3:1 while looking
	// uniform in the source.
	// -------------------------------------------------------------------------

	/** Solid accent fill: primary button, active pill. White text at 4.83:1. */
	'--accent': { light: '#0066FF', dark: '#0066FF' },
	'--accent-hover': { light: '#0058DB', dark: '#0076FF' },
	/** Text on a solid accent fill. */
	'--accent-fg': { light: '#FFFFFF', dark: '#FFFFFF' },
	/** Accent as TEXT: links, nav labels, info status. */
	'--accent-text': { light: '#005EEB', dark: '#579AFF' },
	'--accent-text-hover': { light: '#004BBD', dark: '#94BFFF' },
	/**
	 * Accent tint — an info banner or selected background.
	 *
	 * Authored as rgba rather than a flat hex so a tint composites correctly
	 * over whatever surface it lands on: the same chip sits on a card, a table
	 * row and a modal, and a baked-in hex only matches one of them.
	 */
	'--accent-tint': {
		light: 'rgba(0, 102, 255, 0.13)',
		dark: 'rgba(0, 102, 255, 0.22)',
	},
	'--accent-tint-hover': {
		light: 'rgba(0, 102, 255, 0.20)',
		dark: 'rgba(0, 102, 255, 0.30)',
	},
	/** TRANSITIONAL — a second, paler info tint. Collapses into `--accent-tint`. */
	'--accent-tint-subtle': {
		light: 'rgba(0, 102, 255, 0.07)',
		dark: 'rgba(0, 102, 255, 0.16)',
	},
	/** Text sitting ON an accent tint. */
	'--accent-tint-fg': { light: '#004BBD', dark: '#94BFFF' },
	'--accent-border': { light: '#3D8BFF', dark: '#0F6FFF' },
	/** Outline on a tinted accent/info badge. */
	'--accent-tint-border': { light: '#A8CBFF', dark: '#114593' },
	'--accent-ring': { light: '#0066FF', dark: '#579AFF' },
	/** The soft halo on a focused field. */
	'--accent-ring-soft': {
		light: 'rgba(0, 102, 255, 0.25)',
		dark: 'rgba(87, 154, 255, 0.25)',
	},

	// -------------------------------------------------------------------------
	// Status — success — #00CE7E
	//
	// Bright, and therefore near-black text rather than white. A green dark
	// enough to carry white legibly is a sea green, which is the version that
	// was rejected on sight; keeping the colour and flipping the text is the
	// same trade the warning amber makes.
	// -------------------------------------------------------------------------

	'--success-fg': { light: '#137C53', dark: '#35B684' },
	'--success-tint': {
		light: 'rgba(0, 206, 126, 0.13)',
		dark: 'rgba(0, 206, 126, 0.22)',
	},
	'--success-tint-fg': { light: '#00663E', dark: '#00FF9C' },
	'--success-border': { light: '#02A163', dark: '#2E9E73' },
	/** Solid success fill. Near-black text at 8.89:1. */
	'--success-solid': { light: '#00CE7E', dark: '#00CE7E' },
	'--success-solid-hover': { light: '#00B16C', dark: '#00EF92' },
	/**
	 * Text on a solid success fill. Separate from `--fg-on-fill` for the same
	 * reason `--warning-solid-fg` is: this fill takes near-black, while danger
	 * and the accent keep white.
	 */
	'--success-solid-fg': { light: '#141414', dark: '#141414' },
	/**
	 * ALIAS — the domain status badges ("delivered", "active", "in stock") used
	 * to reach for a brighter green than `--success-solid`, which is how they
	 * ended up pairing white with green-500 at 2.28:1. The approved palette has
	 * one success green, so this now equals the solid and the name is due for
	 * deletion in the badge consolidation.
	 */
	'--success-emphasis': { light: '#00CE7E', dark: '#00CE7E' },
	/** Outline on a tinted success badge. */
	'--success-tint-border': { light: '#A8EED3', dark: '#117558' },
	'--success-ring': { light: '#00CE7E', dark: '#00CE7E' },

	// -------------------------------------------------------------------------
	// Status — danger
	// -------------------------------------------------------------------------

	'--danger-fg': { light: '#DB0022', dark: '#FF667D' },
	/** A heavier danger foreground for validation copy. */
	'--danger-fg-strong': { light: '#A8001A', dark: '#FF94A4' },
	'--danger-tint': {
		light: 'rgba(227, 11, 44, 0.13)',
		dark: 'rgba(227, 11, 44, 0.22)',
	},
	'--danger-tint-hover': {
		light: 'rgba(227, 11, 44, 0.20)',
		dark: 'rgba(227, 11, 44, 0.30)',
	},
	/** TRANSITIONAL — a second, paler danger tint. Collapses into `--danger-tint`. */
	'--danger-tint-subtle': {
		light: 'rgba(227, 11, 44, 0.07)',
		dark: 'rgba(227, 11, 44, 0.16)',
	},
	'--danger-tint-fg': { light: '#A8001A', dark: '#FF94A4' },
	'--danger-border': { light: '#FF3D5B', dark: '#F00025' },
	/** Solid danger fill. Vivid and still carries white, at 4.83:1. */
	'--danger-solid': { light: '#E30B2C', dark: '#E30B2C' },
	'--danger-solid-hover': { light: '#C30926', dark: '#FF0D33' },
	/**
	 * ALIAS — a second solid red that used to be the INVERSE of `--danger-solid`
	 * across themes, because the two were authored separately and never
	 * reconciled. One approved red means they are now the same value; the name
	 * is due for deletion in the badge consolidation.
	 */
	'--danger-emphasis': { light: '#E30B2C', dark: '#E30B2C' },
	/** The darkest solid red — an overdue invoice. */
	'--danger-solid-strong': { light: '#B00722', dark: '#B00722' },
	/** Outline on a tinted danger badge. */
	'--danger-tint-border': { light: '#F5ACB7', dark: '#791B32' },
	'--danger-ring': { light: '#E30B2C', dark: '#FF667D' },
	'--danger-ring-soft': {
		light: 'rgba(227, 11, 44, 0.25)',
		dark: 'rgba(255, 102, 125, 0.25)',
	},

	// -------------------------------------------------------------------------
	// Status — warning — #FFAE2B
	//
	// The product used to carry two hues for "warning", Tailwind yellow and
	// Tailwind amber, and reaching for either was a coin toss. The approved
	// palette has one, so the yellow tokens below are now aliases of the amber
	// ones and are due for deletion in the badge consolidation.
	//
	// Near-black text, like success. Amber dark enough to carry white stops
	// being amber and turns brown.
	// -------------------------------------------------------------------------

	'--warning-fg': { light: '#995F00', dark: '#D28B19' },
	/** ALIAS — was the yellow warning foreground. One hue now. */
	'--warning-fg-alt': { light: '#995F00', dark: '#D28B19' },
	'--warning-tint': {
		light: 'rgba(255, 174, 43, 0.13)',
		dark: 'rgba(255, 174, 43, 0.22)',
	},
	/** ALIAS — was the amber tint against a yellow one. One hue now. */
	'--warning-tint-amber': {
		light: 'rgba(255, 174, 43, 0.13)',
		dark: 'rgba(255, 174, 43, 0.22)',
	},
	'--warning-tint-fg': { light: '#804F00', dark: '#FFCC7A' },
	/**
	 * The border and the focus ring a warning field draws must be the same hue;
	 * before the palette work they were yellow and amber respectively, which is
	 * the collision that made `border-warning` ambiguous in the first place.
	 */
	'--warning-border': { light: '#C77B00', dark: '#FFAE2B' },
	/** Solid warning fill. Near-black text at 9.95:1. */
	'--warning-solid': { light: '#FFAE2B', dark: '#FFAE2B' },
	/**
	 * Kept separate from `--fg-on-fill` because this fill flips to near-black:
	 * white on amber measures ~2:1, the worst pairing in the old set.
	 */
	'--warning-solid-fg': { light: '#141414', dark: '#141414' },
	/** The darker solid amber — a pending payment. */
	'--warning-solid-strong': { light: '#DB9625', dark: '#DB9625' },
	/** ALIAS — was a fainter amber tint. Kept fainter than `--warning-tint`. */
	'--warning-tint-amber-soft': {
		light: 'rgba(255, 174, 43, 0.08)',
		dark: 'rgba(255, 174, 43, 0.16)',
	},
	/** Outline on a tinted warning badge. */
	'--warning-tint-border': { light: '#FFE3B7', dark: '#866631' },
	'--warning-ring': { light: '#FFAE2B', dark: '#FFAE2B' },

	// -------------------------------------------------------------------------
	// Neutral emphasis
	// -------------------------------------------------------------------------

	/** A solid grey badge — "no status" rendered as a status. */
	'--neutral-solid': { light: '#6B7280', dark: '#4B5563' },
	/** TRANSITIONAL — the same idea one step apart. Collapses into the above. */
	'--neutral-solid-alt': { light: '#4B5563', dark: '#6B7280' },
	'--neutral-tint': { light: '#F3F4F6', dark: 'rgba(17, 24, 39, 0.2)' },
	'--neutral-tint-fg': { light: '#1F2937', dark: '#E5E7EB' },
	'--neutral-tint-border': { light: '#E5E7EB', dark: '#1F2937' },
	/** The default focus ring, where no semantic colour applies. */
	'--ring-default': { light: '#6B7280', dark: '#9CA3AF' },

	// -------------------------------------------------------------------------
	// Domain status hues
	//
	// Order / invoice / payment / campaign / lot / stock badges reach for two
	// hues that the four semantic statuses do not cover. They are tokens rather
	// than literals so the domain-status page has something to point at, but
	// they are NOT general-purpose — do not use them for anything else.
	// -------------------------------------------------------------------------

	/** "Open", "scheduled", "medium" — informational. Reuses the accent blue. */
	'--info-solid': { light: '#0066FF', dark: '#0066FF' },
	/** "Shipped" / in transit. */
	'--transit-solid': { light: '#0EA5E9', dark: '#0284C7' },
	/** "Refunded" — the one state that is neither good nor bad. */
	'--refund-solid': { light: '#9333EA', dark: '#A855F7' },

	// -------------------------------------------------------------------------
	// Navigation rail
	//
	// The rail is dark in BOTH themes by design, so these tokens carry the same
	// value twice. They are still tokens rather than literals so a per-tenant
	// brand can move the rail without touching component code.
	// -------------------------------------------------------------------------

	'--nav-bg': { light: '#111827', dark: '#111827' },
	'--nav-bg-hover': { light: '#1F2937', dark: '#1F2937' },
	'--nav-bg-hover-strong': { light: '#374151', dark: '#374151' },
	'--nav-border': { light: '#1F2937', dark: '#1F2937' },
	'--nav-fg': { light: '#D1D5DB', dark: '#D1D5DB' },
	'--nav-fg-active': { light: '#FFFFFF', dark: '#FFFFFF' },
	'--nav-fg-muted': { light: '#9CA3AF', dark: '#9CA3AF' },
	'--nav-fg-strong': { light: '#F3F4F6', dark: '#F3F4F6' },
	/** Unread count on a rail item. Fixed, like the rest of the rail. */
	'--nav-badge-bg': { light: '#EF4444', dark: '#EF4444' },
} as const satisfies Record<string, TokenValue>;

/**
 * The categorical chip palette — 16 hues used to tell unrelated things apart.
 *
 * This is a QUALITATIVE palette, not a semantic one. Chip 7 does not mean
 * anything; it just has to be visually distinct from chips 1–6 and 8–16.
 * `getChipColor(index)` cycles through it, so the whole set is reachable even
 * though no call site names an individual entry — which is exactly why a
 * static "find unused tokens" pass reports fifteen of them as dead. They are
 * not.
 *
 * Kept as a separate table from `designTokens` because the two answer different
 * questions: a semantic token asks "what does this mean", a categorical one
 * asks "which bucket is this". Merging them would invite someone to reach for
 * `--chip-4-bg` because it looked purple.
 */
const CHIP_HUES: ReadonlyArray<{
	name: string;
	bg: TokenValue;
	fg: TokenValue;
	border: TokenValue;
}> = [
	{
		name: 'blue',
		bg: { light: '#93C5FD', dark: '#1E3A8A' },
		fg: { light: '#1E3A8A', dark: '#DBEAFE' },
		border: { light: '#60A5FA', dark: '#1E40AF' },
	},
	{
		name: 'green',
		bg: { light: '#86EFAC', dark: '#14532D' },
		fg: { light: '#14532D', dark: '#DCFCE7' },
		border: { light: '#4ADE80', dark: '#166534' },
	},
	{
		name: 'amber',
		bg: { light: '#FCD34D', dark: '#78350F' },
		fg: { light: '#78350F', dark: '#FEF3C7' },
		border: { light: '#FBBF24', dark: '#92400E' },
	},
	{
		name: 'purple',
		bg: { light: '#D8B4FE', dark: '#581C87' },
		fg: { light: '#581C87', dark: '#F3E8FF' },
		border: { light: '#C084FC', dark: '#6B21A8' },
	},
	{
		name: 'red',
		bg: { light: '#FCA5A5', dark: '#7F1D1D' },
		fg: { light: '#7F1D1D', dark: '#FEE2E2' },
		border: { light: '#F87171', dark: '#991B1B' },
	},
	{
		name: 'pink',
		bg: { light: '#F9A8D4', dark: '#831843' },
		fg: { light: '#831843', dark: '#FCE7F3' },
		border: { light: '#F472B6', dark: '#9D174D' },
	},
	{
		name: 'indigo',
		bg: { light: '#A5B4FC', dark: '#312E81' },
		fg: { light: '#312E81', dark: '#E0E7FF' },
		border: { light: '#818CF8', dark: '#3730A3' },
	},
	{
		name: 'teal',
		bg: { light: '#5EEAD4', dark: '#134E4A' },
		fg: { light: '#134E4A', dark: '#CCFBF1' },
		border: { light: '#2DD4BF', dark: '#115E59' },
	},
	{
		name: 'orange',
		bg: { light: '#FDBA74', dark: '#7C2D12' },
		fg: { light: '#7C2D12', dark: '#FFEDD5' },
		border: { light: '#FB923C', dark: '#9A3412' },
	},
	{
		name: 'sky',
		bg: { light: '#7DD3FC', dark: '#0C4A6E' },
		fg: { light: '#0C4A6E', dark: '#E0F2FE' },
		border: { light: '#38BDF8', dark: '#075985' },
	},
	{
		name: 'violet',
		bg: { light: '#C4B5FD', dark: '#4C1D95' },
		fg: { light: '#4C1D95', dark: '#EDE9FE' },
		border: { light: '#A78BFA', dark: '#5B21B6' },
	},
	{
		name: 'cyan',
		bg: { light: '#67E8F9', dark: '#164E63' },
		fg: { light: '#164E63', dark: '#CFFAFE' },
		border: { light: '#22D3EE', dark: '#155E75' },
	},
	{
		name: 'lime',
		bg: { light: '#BEF264', dark: '#365314' },
		fg: { light: '#365314', dark: '#ECFCCB' },
		border: { light: '#A3E635', dark: '#3F6212' },
	},
	{
		name: 'yellow',
		bg: { light: '#FDE047', dark: '#713F12' },
		fg: { light: '#713F12', dark: '#FEF9C3' },
		border: { light: '#FACC15', dark: '#854D0E' },
	},
	{
		name: 'emerald',
		bg: { light: '#6EE7B7', dark: '#064E3B' },
		fg: { light: '#064E3B', dark: '#D1FAE5' },
		border: { light: '#34D399', dark: '#065F46' },
	},
	{
		name: 'rose',
		bg: { light: '#FDA4AF', dark: '#881337' },
		fg: { light: '#881337', dark: '#FFE4E6' },
		border: { light: '#FB7185', dark: '#9F1239' },
	},
];

/** How many chips the categorical palette carries. `getChipColor` cycles here. */
export const CHIP_COUNT = CHIP_HUES.length;

const chipTokens: Record<string, TokenValue> = {};
CHIP_HUES.forEach((hue, index) => {
	const n = index + 1;
	chipTokens[`--chip-${n}-bg`] = hue.bg;
	chipTokens[`--chip-${n}-fg`] = hue.fg;
	chipTokens[`--chip-${n}-border`] = hue.border;
});

/** Every token this theme layer declares — semantic plus categorical. */
export const allTokens: Record<string, TokenValue> = {
	...designTokens,
	...chipTokens,
};

export type DesignTokenName = keyof typeof designTokens;

/**
 * The deliberate visual changes this token set makes, relative to what the
 * product rendered before it existed.
 *
 * Every entry fixes a case where two roles that are distinct in light mode
 * resolved to the SAME value in dark mode, so the distinction the token exists
 * to express was invisible. Recorded here rather than in a commit message
 * because the verification pass asserts this is the COMPLETE list — any other
 * difference is a regression.
 *
 * Hues are untouched. Every change below is greyscale.
 */
export const TOKEN_CORRECTIONS: ReadonlyArray<{
	token: string;
	theme: 'light' | 'dark';
	from: string;
	to: string;
	callSites: number;
	why: string;
}> = [
	{
		token: 'textSecondary',
		theme: 'dark',
		from: '#9CA3AF',
		to: '#D1D5DB',
		callSites: 4229,
		why: 'Was identical to textMuted in dark, collapsing four emphasis levels to three. Also raises contrast on a card from 5.78:1 to 9.96:1.',
	},
	{
		token: 'bgTertiary',
		theme: 'dark',
		from: '#1F2937',
		to: '#161E2B',
		callSites: 134,
		why: 'Was identical to bgPrimary in dark, so a recessed well was the same colour as the card containing it.',
	},
	{
		token: 'borderStrong',
		theme: 'dark',
		from: '#4B5563',
		to: '#6B7280',
		callSites: 19,
		why: 'Was identical to borderDefault in dark. Moved the rare token rather than the 1,445-call-site one.',
	},
	{
		token: 'bgContainer',
		theme: 'dark',
		from: '#111827',
		to: '#1F2937',
		callSites: 2,
		why: 'Light value is the card surface but dark value was the page canvas, inverting the elevation between themes.',
	},
	{
		token: 'tableHeader',
		theme: 'light',
		from: '#F9FAFB',
		to: '#F3F4F6',
		callSites: 9,
		why: 'A table header is a sunken surface; it now uses the sunken token in both themes instead of matching the row in dark.',
	},
	{
		token: 'tableHeader',
		theme: 'dark',
		from: '#1F2937',
		to: '#161E2B',
		callSites: 9,
		why: 'Same correction — the header was indistinguishable from the rows beneath it.',
	},
	{
		token: 'selectMenu',
		theme: 'dark',
		from: '#1F2937',
		to: '#283244',
		callSites: 5,
		why: 'A dropdown opened over a card had no separation from it. Raised surfaces are now lighter than the surface below.',
	},
	{
		token: 'chartTooltipLabel',
		theme: 'dark',
		from: '#FFFFFF',
		to: '#F3F4F6',
		callSites: 3,
		why: 'Used pure white where every other heading uses the default foreground.',
	},
	{
		token: 'chartTooltipText',
		theme: 'light',
		from: '#374151',
		to: '#4B5563',
		callSites: 1,
		why: 'Used a one-off grey where the supporting-text token applies.',
	},
	{
		token: 'chartTooltipText',
		theme: 'dark',
		from: '#E5E7EB',
		to: '#D1D5DB',
		callSites: 1,
		why: 'Same — folded onto the supporting-text token.',
	},
	{
		token: 'borderHover',
		theme: 'dark',
		from: '#6B7280',
		to: '#6B7280',
		callSites: 1,
		why: 'Unchanged in value; folded onto borderStrong, which now carries the same value.',
	},
	{
		token: 'borderWarning',
		theme: 'light',
		from: '#FACC15',
		to: '#F59E0B',
		callSites: 11,
		why: 'Yellow and amber shared one utility name once colour moved behind a variable. Amber is where the approved palette lands, and it matches the warning ring the same field already draws.',
	},
	{
		token: 'borderWarning',
		theme: 'dark',
		from: '#EAB308',
		to: '#FBBF24',
		callSites: 11,
		why: 'Same correction on the dark side.',
	},
];

/** The CSS custom properties for one theme, ready to write to `:root`. */
export const buildTokenCssVars = (
	mode: 'light' | 'dark',
): Record<string, string> =>
	Object.fromEntries(
		Object.entries(allTokens).map(([name, value]) => [name, value[mode]]),
	);

/** Resolve a single token to its value in one theme. */
export const tokenValue = (
	name: DesignTokenName,
	mode: 'light' | 'dark',
): string => designTokens[name][mode];
